import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, LogOut, Ticket } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { AuthForm } from "../components/site/AuthForm";
import { ChangePassword } from "../components/site/ChangePassword";
import { FrequentManager } from "../components/site/FrequentPassengers";
import { useAuth, homeFor } from "../lib/auth";
import { api, apiError } from "../lib/api";
import { formatARS } from "../lib/site";

const LABEL = { confirmado: "Pagado", reservado: "Sin pagar", pendiente_pago: "Pago pendiente", vencida: "Vencida", cancelado: "Anulado" };

const Reset = ({ token }) => {
  const [pw, setPw] = useState("");
  const [ok, setOk] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token, password: pw }); setOk(true); } catch (err) { toast.error(apiError(err)); }
  };
  if (ok) return <p data-testid="reset-done" className="rounded-2xl bg-[var(--lime)]/30 p-4">Listo, ya podés ingresar con tu contraseña nueva. <Link to="/cuenta" className="font-bold underline">Ingresar</Link></p>;
  return (
    <form onSubmit={save} className="space-y-3">
      <Input data-testid="reset-password" type="password" minLength={8} required placeholder="Contraseña nueva (mínimo 8)" value={pw} onChange={(e) => setPw(e.target.value)} className="h-12 rounded-xl" />
      <button data-testid="reset-submit" className="h-12 w-full rounded-xl bg-[var(--forest)] font-bold text-white">Guardar contraseña</button>
    </form>
  );
};

const MyTrips = () => {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/my/bookings").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([])); }, []);
  if (!list) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!list.length) return <p data-testid="my-trips-empty" className="rounded-2xl bg-[var(--paper)] p-5 text-sm">Todavía no tenés pasajes. <Link to="/pasajes" className="font-bold text-[var(--forest)] underline">Buscar un viaje</Link></p>;
  return (
    <div data-testid="my-trips" className="divide-y divide-black/5 rounded-2xl bg-white ring-1 ring-black/5">
      {list.map((b) => (
        <a key={b.code} href={b.ticket_url} data-testid={`my-trip-${b.code}`} className="flex flex-wrap items-center justify-between gap-3 p-4 transition-colors hover:bg-[var(--paper)]">
          <span><b>{b.origin} → {b.destination}</b><span className="block text-sm text-[var(--muted-ink)]">{b.date} · {b.departure} hs · butacas {(b.seats ?? []).join(", ")}</span></span>
          <span className="flex items-center gap-3 text-sm"><span className="rounded-full bg-[var(--paper)] px-3 py-1 text-xs font-bold">{LABEL[b.status] ?? b.status}</span><span className="font-semibold">{formatARS(b.total)}</span><Ticket className="h-4 w-4 text-[var(--forest)]" /></span>
        </a>
      ))}
    </div>
  );
};

export default function Cuenta() {
  const { user, logout } = useAuth();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const reset = sp.get("reset");
  return (
    <section data-testid="account-page" className="min-h-[75vh] bg-[var(--paper)] px-5 py-14 md:px-10">
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-4xl sm:text-5xl">{user ? `Hola, ${user.name.split(" ")[0]}` : "Mi cuenta"}</h1>
        <div className="mt-8 rounded-[28px] bg-white p-6 ring-1 ring-black/5 md:p-8">
          {reset ? <Reset token={reset} /> : user === null ? <Loader2 className="h-5 w-5 animate-spin" /> : !user ? (
            <AuthForm intro="Para reservar o comprar pasajes necesitás una cuenta. El personal también ingresa acá." onDone={(u) => { if (u?.role && u.role !== "pasajero") navigate(homeFor(u.role)); }} />
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-[var(--muted-ink)]">{user.email}{user.dni && ` · DNI ${user.dni}`}</p>
                <div className="flex gap-2">
                  {user.role !== "pasajero" && <Link to={homeFor(user.role)} className="rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white">Ir al panel</Link>}
                  <ChangePassword />
                  <button onClick={logout} data-testid="account-logout" className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm font-bold"><LogOut className="h-4 w-4" /> Salir</button>
                </div>
              </div>
              <p className="font-display-up text-lg">Mis pasajes</p>
              <MyTrips />
              <p className="pt-4 font-display-up text-lg">Pasajeros frecuentes</p>
              <FrequentManager />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
